import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { CardDisplayService } from "@app/shared/card-display";
import { IColumnData, ITranslateObject, TaxprepT1Card } from "@app/views-interfaces";
import { ColumnDataLayoutService, FiltersService, WkTranslatePipe } from "@shared/index";
import { Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { AnalyticsService, DashboardsService, StepsService } from "../../shared";
import { AuthService } from "../../shared/auth/auth.service";
import { CardViewService } from "../../shared/card-view-service/card-view.service";
import { CountersService } from "../../shared/counters-service/counters.service";
import { HelpersService } from "../../shared/helpers-service/helpers.service";
import { isiFirmTaxDashboard } from "../../views-interfaces/dashboard-model.interface";
import { DragAndDropService } from "@app/drag-and-drop/drag-and-drop.service";

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: "app-not-started",
    templateUrl: "./not-started.component.html",
    styleUrls: ["./../views-common.scss", "./not-started.component.scss"],
    standalone: false,
    host: { class: "wk-ns" },
    providers: [WkTranslatePipe],
})
export class NotStartedComponent implements OnInit, OnDestroy {
    private readonly columnDataLayoutService = inject(ColumnDataLayoutService); // TODO This is injected to perform side-effects, we should refactor and remove
    private readonly analyticsService = inject(AnalyticsService);
    private readonly authService = inject(AuthService);
    private readonly cardDisplayService = inject(CardDisplayService);
    private readonly cardViewService = inject(CardViewService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly filtersService = inject(FiltersService);
    private readonly helpersService = inject(HelpersService);
    private readonly stepsService = inject(StepsService);
    protected readonly countersService = inject(CountersService);
    private readonly dragAndDropService = inject(DragAndDropService);

    private readonly subscription = new Subscription();
    selectedStepId = "all_steps_not_started";
    printing = false;
    displayMode = "Kanban";
    numberOfReturn = 0;
    public isiFirmDashboard: boolean;
    public userCanPrintExport = false;
    public showLastYearReceivedDateColumn: boolean;
    isLoadingColumn = false;
    protected columns: IColumnData<TaxprepT1Card>[] = [];
    private rawColumns: IColumnData<TaxprepT1Card>[] = [];
    listPillInfo: {
        id: string;
        columnName: string | ITranslateObject;
    }[];

    constructor() {}

    ngOnInit() {
        this.cardViewService.setCurrentViewType("notStarted");
        this.analyticsService.track("Viewed Not started returns");
        this.init();

        this.subscription.add(
            this.cardViewService.viewDisplayModeChangeEvent$.subscribe(() => {
                this.displayMode = this.cardViewService.currentViewDisplayMode;
                if (this.displayMode !== "List") {
                    this.selectedStepId = "all_steps_not_started";
                }
                this.initColumns();
            }),
        );
        this.subscription.add(
            this.dashboardsService.currentDashboardChanged$.subscribe(() => {
                // Synchronously invalidate stale column data to avoid filters applied to wrong card types on dashboard switch (DASH-21677)
                this.rawColumns = [];
                this.columns = [];
                this.init();
            }),
        );
        this.subscription.add(
            this.cardDisplayService.cardChangedEvent$.pipe(debounceTime(200), this.dragAndDropService.deferWhileDragging()).subscribe(() => {
                this.initColumns();
            }),
        );
        this.subscription.add(
            this.filtersService.filtersChangedEvent$.pipe(this.dragAndDropService.deferWhileDragging()).subscribe(() => {
                this.applyCardFilters();
            }),
        );
    }

    ngOnDestroy() {
        this.subscription?.unsubscribe();
    }

    async init() {
        await this.filtersService.refreshFiltersDependencies();
        this.isLoadingColumn = true;
        this.userCanPrintExport = this.authService.currentUser.can_print_export === "True";
        const currentDashboard = await this.dashboardsService.getCurrentDashboard();
        const key = this.helpersService.getCurrentUser().sub + currentDashboard.Id + currentDashboard.TaxYear + this.cardViewService.currentViewType;
        const recentView = localStorage.getItem(key);
        this.displayMode = recentView ?? "Kanban";
        this.isiFirmDashboard = isiFirmTaxDashboard(currentDashboard);
        this.showLastYearReceivedDateColumn = !this.isiFirmDashboard || (this.isiFirmDashboard && currentDashboard.TaxYear >= 2025);
        this.cardViewService.selectedStepId = "all_steps_not_started";
        await this.initPills();
        await this.initColumns();
        this.isLoadingColumn = false;
    }

    async initPills() {
        const steps = await this.stepsService.getStepsList();
        this.listPillInfo = [
            {
                id: "all_steps_not_started",
                columnName: "Dashboard.AllNotStartedReturns",
            },
            ...steps
                .filter((step) => step.State === "notStarted")
                .map((step) => {
                    return {
                        id: step.Id,
                        columnName: step.Name,
                    };
                }),
        ];
    }

    async initColumns() {
        const cardModel = await this.cardDisplayService.getModel();
        const steps = await this.stepsService.getStepsList();
        await this.helpersService.mergeSteps(); // TODO return this thing where it came back or so help me
        const mergedSteps = this.helpersService.getMergedSteps(); // TODO return this thing where it came back or so help me

        const perStepColumns: IColumnData<TaxprepT1Card>[] =
            steps
                ?.filter((step) => step.State === "notStarted")
                .map((step) => {
                    const statuses = mergedSteps
                        .find((mergedStep) => mergedStep.Id === step.Id)
                        ?.Statuses.filter((status) => status.TaxReturnStatus != null);
                    const stepStatusIds = new Set<string | null | undefined>((statuses ?? []).map((status) => status.TaxReturnStatus.Id));
                    return {
                        id: step.Id,
                        columnName: step.Name,
                        statuses: statuses,
                        state: step.State,
                        isCustomStatus: false,
                        fromCache: false,
                        cards: cardModel
                            .filter((model) => stepStatusIds.has(model.statusId))
                            .flatMap((model) => model.cards as TaxprepT1Card[]),
                    } as IColumnData<TaxprepT1Card>;
                }) ?? [];

        if (this.displayMode === "List" && this.selectedStepId === "all_steps_not_started") {
            this.rawColumns = [
                {
                    id: "all_steps_not_started",
                    columnName: "Dashboard.AllNotStartedReturns",
                    statuses: perStepColumns.flatMap((column) => column.statuses ?? []),
                    state: "notStarted",
                    isCustomStatus: false,
                    fromCache: false,
                    cards: perStepColumns.flatMap((column) => column.cards),
                } as IColumnData<TaxprepT1Card>,
            ];
        } else {
            this.rawColumns = perStepColumns.filter((column) => this.selectedStepId === "all_steps_not_started" || column.id === this.selectedStepId);
        }
        this.applyCardFilters();
        this.updateNumberOfReturn();
    }

    private applyCardFilters(): void {
        this.columns = this.rawColumns.map((column) => {
            const cards = this.filtersService.filterCards(column.cards) as TaxprepT1Card[];
            this.filtersService.sortCards(cards);
            return { ...column, cards };
        });
    }

    stepSelection(stepId: string, statusName: string | ITranslateObject) {
        this.analyticsService.track("Viewed Not Started - Changed steps", {
            Step: typeof statusName === "string" ? statusName : statusName["en-CA"],
        });
        this.selectedStepId = stepId;
        this.cardViewService.selectedStepId = stepId;
        this.initColumns();
    }

    printPage() {
        if (!this.printing) {
            this.analyticsService.track("Printed Not started view");
            this.printing = true;
            setTimeout(() => {
                this.printing = false;
            }, 1000);
        }
    }

    updateNumberOfReturn() {
        // TODO now this loads too fast and we have to put a condition here
        if (this.countersService.statisticsModel) {
            this.numberOfReturn = this.countersService.statisticsModel
                .filter(
                    (model) =>
                        model.stepId === this.selectedStepId || (this.selectedStepId === "all_steps_not_started" && model.state === "notStarted"),
                )
                .reduce((sum, model) => sum + model.count, 0);
        }
    }
}
